import { useMemo } from 'react';
import { useClinicians } from '@features/organizations/store/hooks';

import { useOrgScheduleAccess } from './useOrgScheduleAccess';
import { useOrgScheduleFilters } from './useOrgScheduleFilters';

/**
 * Builds a provider_id → provider name map, gated on org view.
 *
 * When org view is inactive the map is empty, so consumers can
 * simply do `providerMap.get(id)` — undefined means "don't render".
 * RTK Query cache means zero extra fetches when the filter bar
 * already triggered the clinicians query.
 */
export function useProviderNameMap(): Map<string, string> {
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { isOrgViewActive } = useOrgScheduleFilters();
  const isActive = hasOrgScheduleAccess && isOrgViewActive;

  const { clinicians } = useClinicians({ skip: !isActive });

  return useMemo(() => {
    if (!isActive || !clinicians.length) return new Map<string, string>();
    return new Map<string, string>(clinicians.map(c => [c.userId, c.name]));
  }, [isActive, clinicians]);
}
